import { type RefObject } from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { SvgIconComponent } from "@mui/icons-material";
import { FONT_MONO } from "@/constants";

interface FileUploadSlotProps {
  file: File | null;
  inputRef: RefObject<HTMLInputElement | null>;
  accept: string;
  icon: SvgIconComponent;
  label: string;
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

export function FileUploadSlot({
  file,
  inputRef,
  accept,
  icon: Icon,
  label,
  onSelect,
  onRemove,
}: FileUploadSlotProps) {
  const handleClick = () => {
    if (file) {
      onRemove();
    } else {
      inputRef.current?.click();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={onSelect}
      />
      <Box
        onClick={handleClick}
        sx={{
          width: "100%",
          minWidth: 0,
          aspectRatio: "1",
          border: "2px dashed",
          borderColor: file ? "primary.main" : "divider",
          borderRadius: 2,
          bgcolor: file ? "primary.main" : "action.hover",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          boxSizing: "border-box",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: file ? "primary.dark" : "action.selected",
          },
        }}
      >
        {file ? (
          <>
            <CloseIcon sx={{ color: "#fff", fontSize: 20, mb: 0.5 }} />
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                fontSize: 10,
                textAlign: "center",
                px: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {file.name}
            </Typography>
          </>
        ) : (
          <>
            <Icon sx={{ color: "text.disabled", fontSize: 24 }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: 10,
                fontFamily: FONT_MONO,
              }}
            >
              {label}
            </Typography>
          </>
        )}
      </Box>
    </>
  );
}
